import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, Minus } from "lucide-react";
import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { cn } from "@/shared/lib/utils";

/**
 * Radix Checkbox wrapper. Square hit target (`h-5 w-5`), `rounded-md`-free
 * per design rules: uses `rounded-sm` for the small inner shape that reads
 * as a checkbox. Glass-subtle surface at rest, brand fill when checked.
 *
 * Supports tri-state via `checked="indeterminate"` (Radix native) — the
 * indicator renders a `Minus` glyph in that case so the header checkbox
 * in tables can communicate partial selection.
 */
export const Checkbox = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(function Checkbox({ className, ...props }, ref) {
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        "flex h-5 w-5 shrink-0 items-center justify-center",
        "rounded-sm border border-border glass-surface-subtle",
        "transition-colors duration-150",
        "data-[state=checked]:bg-brand data-[state=checked]:border-brand data-[state=checked]:text-text-inverse",
        "data-[state=indeterminate]:bg-brand data-[state=indeterminate]:border-brand data-[state=indeterminate]:text-text-inverse",
        "disabled:opacity-50 disabled:pointer-events-none",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center">
        {props.checked === "indeterminate" ? (
          <Minus aria-hidden className="h-3.5 w-3.5" />
        ) : (
          <Check aria-hidden className="h-3.5 w-3.5" />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});
